import { read, applyProperties, applyArtwork } from '@theme/product-reorder-prefill';

/**
 * @typedef {Object} SbWholesaleRefs
 * @property {HTMLImageElement} [productImage]
 * @property {HTMLElement} productPrice
 * @property {HTMLInputElement} variantId
 * @property {HTMLInputElement} prevPrice
 * @property {HTMLInputElement} productName
 * @property {HTMLInputElement} [priceMultiplier]
 * @property {HTMLSelectElement} [qtySelect]
 * @property {HTMLElement} [materialOptionsWrap]
 * @property {HTMLButtonElement} addToCartBtn
 * @property {HTMLElement} addToCartText
 * @property {HTMLButtonElement} [sampleBtn]
 */

const ICEPICK_WHOLESALE_URL = 'https://icepick.co/api/shopify/wholesale.php';

/**
 * Formats a Shopify price (in cents) as a dollar string.
 * @param {number} cents
 * @returns {string}
 */
const formatMoneyFromCents = (cents) => {
  const dollars = (cents / 100).toFixed(2);
  return `$${Number(dollars).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Formats a dollar value as a currency string.
 * @param {string|number} dollarStr
 * @returns {string}
 */
const formatMoneyFromDollars = (dollarStr) => {
  const val = parseFloat(String(dollarStr).replace(/[^0-9.-]/g, ''));
  if (isNaN(val)) return '$0.00';
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

class SbWholesaleForm extends HTMLElement {
  /** @type {Array<Object>} */
  #variants = [];
  /** @type {number} price in cents */
  #basePrice = 0;
  /** @type {Object|null} materials metafield data */
  #materialsData = null;

  connectedCallback() {
    this.#parseDataAttributes();
    this.#cacheRefs();
    this.#buildMaterialOptions();
    this.#bindEvents();
    this.#bindReviews();
    this.#handleLocalStorage();
    this.#prefillFromUrl();
  }

  /**
   * Reflects a re-order deep-link (see @theme/product-reorder-prefill): restores the
   * variant option selects, quantity and line-item properties, then re-derives
   * the matched variant / price via the existing option-change handler.
   */
  #prefillFromUrl() {
    const { params, active } = read();
    if (!active || !params) return;

    const variant = params.get('variant');
    if (variant) {
      this.refs.variantId.value = variant;
      const matched = this.#variants.find((v) => String(v.id) === variant);
      if (matched && Array.isArray(matched.options)) {
        const selects = this.querySelectorAll('.sb-pdp-ws__option-select');
        selects.forEach((select, i) => {
          if (matched.options[i] != null) select.value = matched.options[i];
        });
      }
    }

    const qty = params.get('quantity');
    if (qty && this.refs.qtySelect) this.refs.qtySelect.value = qty;

    applyProperties(this, params);
    applyArtwork(this, params);

    this.#handleOptionChange();
  }

  #parseDataAttributes() {
    this.#basePrice = parseInt(this.dataset.basePrice, 10) || 0;

    const variantsRaw = this.dataset.variants;
    if (variantsRaw) {
      try { this.#variants = JSON.parse(variantsRaw); } catch { this.#variants = []; }
    }

    const materialsRaw = this.dataset.materials;
    if (materialsRaw) {
      try { this.#materialsData = JSON.parse(materialsRaw); } catch { this.#materialsData = null; }
    }
  }

  #cacheRefs() {
    /** @type {SbWholesaleRefs} */
    this.refs = {};
    const refEls = this.querySelectorAll('[ref]');
    for (const el of refEls) {
      const name = el.getAttribute('ref');
      if (name) this.refs[name] = el;
    }
  }

  /* Material options (price multiplier)
     ========================================================================== */

  #buildMaterialOptions() {
    if (!this.#materialsData || !this.refs.materialOptionsWrap) return;

    for (const [option, values] of Object.entries(this.#materialsData)) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'sb-pdp-ws__fieldset';

      const legend = document.createElement('legend');
      legend.className = 'sb-pdp-ws__label';
      legend.textContent = option;
      fieldset.appendChild(legend);

      const wrap = document.createElement('div');
      wrap.className = 'sb-pdp-ws__select-wrap';

      const select = document.createElement('select');
      select.name = `properties[${option}]`;
      select.className = 'sb-pdp-ws__select sb-pdp-ws__multiplier-select';
      select.dataset.priceMultiplierSelect = option.toLowerCase();
      select.setAttribute('aria-label', option);

      for (const [key, value] of Object.entries(values)) {
        const optionEl = document.createElement('option');
        optionEl.value = key;
        optionEl.dataset.priceMultiplier = String(value);
        optionEl.textContent = key;
        select.appendChild(optionEl);
      }

      wrap.appendChild(select);

      const icon = document.createElement('span');
      icon.className = 'sb-pdp-ws__select-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      wrap.appendChild(icon);

      fieldset.appendChild(wrap);
      this.refs.materialOptionsWrap.appendChild(fieldset);
    }
  }

  /* Event binding
     ========================================================================== */

  #bindEvents() {
    const optionSelects = this.querySelectorAll('.sb-pdp-ws__option-select');
    for (const select of optionSelects) {
      select.addEventListener('change', () => this.#handleOptionChange());
    }

    const multiplierSelects = this.querySelectorAll('.sb-pdp-ws__multiplier-select');
    for (const select of multiplierSelects) {
      select.addEventListener('change', () => this.#handleMultiplierChange());
    }

    this.refs.qtySelect?.addEventListener('change', () => this.#handleQtyChange());

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('submit', (e) => this.#handleSubmit(e));
    }

    this.refs.sampleBtn?.addEventListener('click', () => this.#handleSampleAdd());
  }

  #bindReviews() {
    const initializeReviewAnchor = () => {
      const reviewEl = this.querySelector(".rating-wrapper");
      const reviewsWidget = document.querySelector('div[id*="stamped_reviews_main_widget"]');
      const reviewBannerBtn = Array.from(document.querySelectorAll('a[href*="#reviews-widget"]'))
      if (!reviewsWidget) return;

      if (reviewEl) {
        reviewEl.style.cursor = 'pointer';
        reviewEl.addEventListener('click', () => {
          reviewsWidget.scrollIntoView({behavior: 'smooth'});
        })
      }

      if (reviewBannerBtn) {
        reviewBannerBtn.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            reviewsWidget.scrollIntoView({behavior: 'smooth'});
          })
        })
      }
    }

    if (document.readyState !== 'loading') {
      initializeReviewAnchor();
    } else {
      window.addEventListener('DOMContentLoaded', initializeReviewAnchor);
    }
  }

  /* localStorage cart open behaviour
     ========================================================================== */

  #handleLocalStorage() {
    const lpv = localStorage.getItem('stickerbors_lpv');
    if (lpv === 'cart') {
      setTimeout(() => {
        document.body.classList.add('cart_open');
      }, 300);
    }
    const productUrl = this.dataset.productUrl;
    if (productUrl) {
      localStorage.setItem('stickerbors_lpv', productUrl);
    }
  }

  /* Option change: variant matching
     ========================================================================== */

  #handleOptionChange() {
    const selectedOptions = [];
    const optionSelects = this.querySelectorAll('.sb-pdp-ws__option-select');
    for (const select of optionSelects) {
      selectedOptions.push(select.value);
    }

    const matched = this.#variants.find(
      (v) => v.options.toString() === selectedOptions.toString()
    );

    if (matched) {
      this.refs.variantId.value = String(matched.id);
      this.refs.productPrice.textContent = formatMoneyFromCents(matched.price);
      this.refs.prevPrice.value = formatMoneyFromCents(matched.price);

      if (matched.featured_image && this.refs.productImage) {
        this.refs.productImage.src = matched.featured_image.src;
      }
    } else {
      this.refs.variantId.value = '0';
    }

    this.#syncQtyFromOption();
    this.#syncBundlePrice();
  }

  #syncQtyFromOption() {
    const qtyOptionSelect = this.querySelector('[data-select-option="quantity"]');
    if (!qtyOptionSelect) return;

    const selected = qtyOptionSelect.options[qtyOptionSelect.selectedIndex];
    const qtyValue = selected?.dataset.qtyValue;
    if (qtyValue && this.refs.qtySelect) {
      this.refs.qtySelect.value = qtyValue;
    }
  }

  #syncBundlePrice() {
    const bundleSelect = this.querySelector('[data-select-option="bundle-price"]');
    if (!bundleSelect) return;

    const qty = this.#getQty();
    const bundle = qty >= 6 ? '6+' : '1-5';
    bundleSelect.value = bundle;
    bundleSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* Quantity change (select dropdown)
     ========================================================================== */

  #handleQtyChange() {
    this.#syncBundlePrice();
  }

  /* Multiplier change
     ========================================================================== */

  #handleMultiplierChange() {
    const select = this.querySelector('.sb-pdp-ws__multiplier-select');
    if (!select) return;

    const selected = select.options[select.selectedIndex];
    const multiplier = parseFloat(selected?.dataset.priceMultiplier) || 0;

    if (this.refs.priceMultiplier) {
      this.refs.priceMultiplier.value = String(multiplier);
    }

    const prevPriceStr = this.refs.prevPrice?.value || '0';
    const prevPriceDollars = parseFloat(prevPriceStr.replace(/[^0-9.-]/g, '')) || 0;
    const newPrice = prevPriceDollars * multiplier;

    if (newPrice > 0) {
      this.refs.productPrice.textContent = formatMoneyFromDollars(newPrice);
    }
  }

  /* Quantity helper
     ========================================================================== */

  #getQty() {
    if (this.refs.qtySelect) {
      return parseInt(this.refs.qtySelect.value, 10) || 1;
    }
    return 1;
  }

  /* Form submission
     ========================================================================== */

  /** @param {SubmitEvent} e */
  async #handleSubmit(e) {
    e.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const btn = this.refs.addToCartBtn;
    if (btn) btn.disabled = true;
    this.#setButtonText('Processing...');

    try {
      const isApiForm = form.classList.contains('sb-pdp-ws__form--api');

      if (isApiForm && this.refs.variantId?.value === '0') {
        await this.#createIcepickVariant(form);
      }

      if (!this.refs.variantId?.value || this.refs.variantId.value === '0') {
        throw new Error('No variant selected');
      }

      await this.#addToCartAndOpenDrawer(form);
    } catch (error) {
      console.error('Add to cart failed:', error);
      if (btn) btn.disabled = false;
      this.#setButtonText('Add to Cart');
    }
  }

  /**
   * POST the full form to /cart/add.js, dispatch cart:update, and open the cart drawer.
   * @param {HTMLFormElement} form
   */
  async #addToCartAndOpenDrawer(form) {
    const btn = this.refs.addToCartBtn;
    const formData = new FormData(form);

    const cartRes = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
    });

    if (!cartRes.ok) throw new Error(`Cart add failed: HTTP ${cartRes.status}`);

    this.#setButtonText('Added!');
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: {} } }));

    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }

    setTimeout(() => {
      if (btn) btn.disabled = false;
      this.#setButtonText('Add to Cart');
    }, 2000);
  }

  /**
   * When the variant is not found (id=0) and the product has a price multiplier,
   * POST to the icepick wholesale API to create a variant on the fly.
   * @param {HTMLFormElement} form
   */
  async #createIcepickVariant(form) {
    const formData = new FormData(form);
    const formObj = {};
    for (const [key, value] of formData.entries()) {
      formObj[key] = value;
    }

    const body = new URLSearchParams();
    body.append('form_data', JSON.stringify(formObj));

    const res = await fetch(`${ICEPICK_WHOLESALE_URL}?v=${Date.now()}`, {
      method: 'POST',
      body,
    });

    const text = await res.text();
    let data;
    try { data = JSON.parse(text); } catch { throw new Error('Unexpected server response'); }

    if (data.variant?.id) {
      this.refs.variantId.value = String(data.variant.id);
      return;
    }

    if (data.error) throw new Error(data.error);
    throw new Error('Failed to create variant');
  }

  /** @param {string} text */
  #setButtonText(text) {
    if (this.refs.addToCartText) {
      this.refs.addToCartText.textContent = text;
    }
  }

  /* Sample product add
     ========================================================================== */

  async #handleSampleAdd() {
    const variantId = this.refs.sampleBtn?.dataset.variantId;
    if (!variantId) return;

    const btn = this.refs.sampleBtn;
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding...';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed');

      btn.textContent = 'Added!';
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { data: {} } }));

      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = originalText;
      }, 2000);
    } catch (error) {
      console.error('Sample add failed:', error);
      btn.disabled = false;
      btn.textContent = originalText;
    }
  }
}

customElements.define('sb-wholesale-form', SbWholesaleForm);
